import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { ReportTypeViewStore } from "../../store";
import { ReportTypeView } from "../view/ReportTypeView";
import { ReportTypeUpdateStore } from "../../store";
import { ReportTypeUpdate } from "../update/ReportTypeUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type ReportTypeModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const ReportTypeModalContent: React.FC<ReportTypeModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new ReportTypeUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new ReportTypeViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <ReportTypeView
            reportTypeViewStore={storeInfo.store as ReportTypeViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <ReportTypeUpdate
            reportTypeUpdateStore={storeInfo.store as ReportTypeUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
